// IMPORTANT: After modifying this file, update CHANGELOG.md with a summary of your changes.
// Add clear comments for non-obvious changes, including what replaced prior behavior, to avoid repeating failed fixes.

"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import type { AnchorHTMLAttributes } from "react";

// Anchor props only. The blocks never pass Link-only props (prefetch, scroll),
// and keeping them out means nothing unknown reaches the DOM on the `<a>` branch.
type Props = AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

/**
 * True when `href` points at a fragment on the page the visitor is already on:
 * a bare `#book`, or `/rescue#book` while the pathname is `/rescue`.
 * Exported so the decision is unit-testable without rendering.
 */
export function isSamePageHash(href: string, pathname: string): boolean {
  const hashAt = href.indexOf("#");
  if (hashAt < 0) return false;
  const path = href.slice(0, hashAt);
  if (path === "") return true;
  // Compare without trailing slashes so `/rescue/` and `/rescue` match.
  const strip = (p: string) => p.replace(/\/+$/, "") || "/";
  return strip(path) === strip(pathname);
}

/**
 * `next/link` treats a click whose target equals the current URL, hash
 * included, as a no-op: after the first click on `#book` the URL is already
 * `/rescue#book`, so later clicks do not scroll. A native anchor re-runs the
 * fragment scroll on every click, so same-page hash links render as `<a>`.
 * Everything else keeps `Link` and its prefetching.
 */
export function SmartLink({ href, ...rest }: Props) {
  const pathname = usePathname();
  if (isSamePageHash(href, pathname)) {
    return <a href={href} {...rest} />;
  }
  return <Link href={href} {...rest} />;
}
